import { ChevronLeft, ChevronRight } from 'lucide-react-native';
import { twJoin } from 'tailwind-merge';

import type { ResultStep } from './resultStep';

import { GlassSurface } from '@/components/ui/glass';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';

/** Height of the floating pill. */
const PILL_HEIGHT = 48;

/** Gap between the pill and the edge it floats above, and between it and the last line of content. */
const PILL_GAP = 12;

/**
 * Bottom padding a scroll view needs so its last line can scroll clear of the
 * floating footer. `bottomInset` is whatever the host has not already padded,
 * such as the home indicator on the pushed result screen.
 */
export function resultNavigationClearance(bottomInset: number) {
  return PILL_HEIGHT + PILL_GAP * 2 + bottomInset;
}

function StepButton({
  icon,
  accessibilityLabel,
  disabled,
  onPress,
}: {
  icon: typeof ChevronLeft;
  accessibilityLabel: string;
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      hitSlop={ICON_BUTTON_HIT_SLOP}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled }}
      className={twJoin(
        'h-10 w-10 items-center justify-center rounded-full',
        disabled ? 'opacity-40' : 'web:cursor-pointer web:hover:bg-background-100'
      )}>
      <Icon as={icon} className="h-5 w-5 text-typography-900" />
    </Pressable>
  );
}

/**
 * Steps between the related inquiries a Research turn found, one result at a
 * time, without going back to the thread. Buttons rather than a swipe
 * carousel: post bodies are long and scroll, which fights a horizontal-paging
 * gesture the way it wouldn't for a short fixed-height card.
 *
 * A pill floating over the bottom of the post, so it stays in reach however
 * far the reader has scrolled. It is Liquid Glass on iOS 26 and an opaque
 * bordered pill elsewhere; the host must pad its scroll content by
 * `resultNavigationClearance` so the last line is not trapped under it. The
 * step buttons are plain rather than `GlassIconButton`, since glass circles
 * drawn on a glass pill read as a second layer of chrome.
 *
 * `itemNoun` names what one step is, for the labels a screen reader hears. The
 * reference sheet pages references with this same pill, so paging looks the
 * same wherever a reader meets it.
 */
export function ResultNavigation({
  step,
  bottomInset = 0,
  itemNoun = 'inquiry',
}: {
  step: ResultStep;
  bottomInset?: number;
  itemNoun?: string;
}) {
  const { index, count, onStep } = step;

  const capitalizedNoun = `${itemNoun.charAt(0).toUpperCase()}${itemNoun.slice(1)}`;

  return (
    <View
      pointerEvents="box-none"
      className="absolute bottom-0 left-0 right-0 items-center"
      style={{ paddingBottom: bottomInset + PILL_GAP }}>
      <GlassSurface
        isInteractive
        className="flex-row items-center gap-1 rounded-full px-1"
        fallbackClassName="border border-outline-500 bg-background-0 shadow-soft-2"
        style={{ height: PILL_HEIGHT }}>
        <StepButton
          icon={ChevronLeft}
          accessibilityLabel={`Previous ${itemNoun}`}
          disabled={index === 0}
          onPress={() => onStep('previous')}
        />

        <Text
          className="min-w-16 text-center font-medium text-sm text-typography-900"
          accessibilityLiveRegion="polite"
          accessibilityLabel={`${capitalizedNoun} ${index + 1} of ${count}`}>
          {index + 1} of {count}
        </Text>

        <StepButton
          icon={ChevronRight}
          accessibilityLabel={`Next ${itemNoun}`}
          disabled={index === count - 1}
          onPress={() => onStep('next')}
        />
      </GlassSurface>
    </View>
  );
}
